import React from "react";
import { HOMESTAY } from "@/lib/data";
import { Flame, UtensilsCrossed } from "lucide-react";

const addons = [
  {
    icon: Flame,
    title: "Bonfire / Firepit",
    desc: "Firewood for a 2-hour bonfire under the stars.",
    price: "₹300",
  },
  {
    icon: UtensilsCrossed,
    title: "Barbeque",
    desc: "Charcoal for a 2-hour BBQ session.",
    price: "₹300",
  },
];

export default function Pricing() {
  return (
    <section id="pricing" data-testid="pricing-section" className="bg-mrn-bg py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <p className="text-xs uppercase tracking-[0.3em] text-mrn-terracotta font-medium">Pricing</p>
          <h2 className="mt-4 headline-display text-4xl md:text-6xl text-mrn-wood">
            Transparent <em className="font-light text-mrn-green">& honest.</em>
          </h2>
          <p className="mt-6 text-mrn-wood-soft max-w-lg">
            A single price for the whole house. No per-head nonsense. Bring your
            tribe of up to 12.
          </p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 gap-5">
          <div
            data-testid="price-card-weekday"
            className="rounded-2xl bg-white border border-mrn-sand-dark/40 p-8 md:p-10"
          >
            <p className="text-xs uppercase tracking-[0.3em] text-mrn-wood-soft">Mon – Thu</p>
            <h3 className="mt-3 font-display text-3xl text-mrn-wood">Weekday</h3>
            <div className="mt-8 flex items-baseline gap-2">
              <span className="font-display text-6xl md:text-7xl text-mrn-wood">
                {HOMESTAY.pricing.weekday.split(" + ")[0]}
              </span>
              <span className="text-mrn-wood-soft text-sm">+ GST / night</span>
            </div>
            <p className="mt-3 text-sm text-mrn-wood-soft">For the entire villa</p>
          </div>

          <div
            data-testid="price-card-weekend"
            className="rounded-2xl bg-mrn-wood text-mrn-cream p-8 md:p-10 relative overflow-hidden"
          >
            <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-mrn-terracotta/30 blur-3xl" />
            <p className="text-xs uppercase tracking-[0.3em] text-mrn-amber">Fri – Sun</p>
            <h3 className="mt-3 font-display text-3xl">Weekend</h3>
            <div className="mt-8 flex items-baseline gap-2">
              <span className="font-display text-6xl md:text-7xl">
                {HOMESTAY.pricing.weekend.split(" + ")[0]}
              </span>
              <span className="text-mrn-sand text-sm">+ GST / night</span>
            </div>
            <p className="mt-3 text-sm text-mrn-sand">For the entire villa</p>
          </div>
        </div>

        <div className="mt-10 rounded-2xl border border-mrn-sand-dark/40 bg-white/60 p-6 md:p-8" data-testid="addons-block">
          <p className="text-xs uppercase tracking-[0.3em] text-mrn-terracotta font-medium">Add-ons</p>
          <h3 className="mt-3 font-display text-2xl md:text-3xl text-mrn-wood">
            Make the evening, <em className="font-light text-mrn-green">on request.</em>
          </h3>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {addons.map((a, i) => (
              <div
                key={a.title}
                data-testid={`addon-${i}`}
                className="flex items-start gap-4 rounded-xl bg-white border border-mrn-sand-dark/30 p-5"
              >
                <div className="shrink-0 rounded-full bg-mrn-terracotta/15 text-mrn-terracotta p-2.5">
                  <a.icon size={20} strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-3">
                    <h4 className="font-display text-xl text-mrn-wood">{a.title}</h4>
                    <span className="font-display text-2xl text-mrn-terracotta whitespace-nowrap">
                      {a.price}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-mrn-wood-soft">{a.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs text-mrn-wood-soft">
            Add-ons are billed per session. Just let us know at the time of
            booking and we'll have everything set up for you.
          </p>
        </div>

        <p
          className="mt-8 text-xs md:text-sm text-mrn-wood-soft leading-relaxed max-w-3xl"
          data-testid="security-deposit-note"
        >
          <span className="text-mrn-terracotta font-medium">*</span> Security
          deposit of <strong className="text-mrn-wood">{HOMESTAY.pricing.security_deposit}</strong>{" "}
          collected at check-in. The deposit will be refunded after check-out
          unless the place requires deep cleaning due to excessive mess, or any
          items/furniture at the property are misplaced or damaged.
        </p>
      </div>
    </section>
  );
}
